import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faEnvelope,
  faPhone,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { SITE_ORIGIN } from "../../config/appConfig";
import { FAVICON_URL, cdnAssetUrl } from "../../config/staticAssets";
import "./TermsAndCondition.css";

const PAGE_META = {
  title: "Terms and Conditions | Welinkjobs",
  description:
    "Users can search for and apply to job opportunities globally through an interactive platform.",
  keywords: "HTML, CSS, JavaScript",
  author: "WeLinkJobs LLC",
  canonicalPath: "/terms-and-conditions",
  siteName: "Welinkjobs",
  themeColor: "#012660",
};

const NAV_LINKS = [
  { label: "About", href: "/about", active: true },
  { label: "Blogs", href: "https://welinkjobs.com/blogs", external: true },
  { label: "Contact", href: "/about#contactform" },
  { label: "FAQs", href: "/faq.html" },
  { label: "Download App", href: "/downlaodapp.html" },
];

function TermsAndCondition() {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [headerFixed, setHeaderFixed] = useState(false);

  const canonicalUrl = `${SITE_ORIGIN}${PAGE_META.canonicalPath}`;
  const pageUrl = useMemo(() => {
    if (typeof window === "undefined") return canonicalUrl;
    return `${window.location.origin}${pathname}`;
  }, [canonicalUrl, pathname]);

  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > 280);
      setHeaderFixed(window.scrollY > 100);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const html = document.documentElement;
    const prevTheme = html.getAttribute("data-theme");
    const prevBsTheme = html.getAttribute("data-bs-theme");
    const hadDarkMode = document.body.classList.contains("darkMode");
    const hadLightMode = document.body.classList.contains("lightMode");

    html.setAttribute("data-theme", "light");
    html.setAttribute("data-bs-theme", "light");
    document.body.classList.add("wlj-terms-page-active");
    document.body.classList.remove("darkMode");
    document.body.classList.add("lightMode");

    return () => {
      document.body.classList.remove("wlj-terms-page-active");
      if (prevTheme) html.setAttribute("data-theme", prevTheme);
      else html.removeAttribute("data-theme");
      if (prevBsTheme) html.setAttribute("data-bs-theme", prevBsTheme);
      else html.removeAttribute("data-bs-theme");
      document.body.classList.toggle("darkMode", hadDarkMode);
      document.body.classList.toggle("lightMode", hadLightMode);
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const renderNavLink = (item, onClick) => {
    const content = <span>{item.label}</span>;
    if (item.external) {
      return (
        <a href={item.href} target="_blank" rel="noreferrer" onClick={onClick}>
          {content}
        </a>
      );
    }
    if (item.href.startsWith("/")) {
      return (
        <Link to={item.href} onClick={onClick}>
          {content}
        </Link>
      );
    }
    return (
      <a href={item.href} onClick={onClick}>
        {content}
      </a>
    );
  };

  return (
    <>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <title>{PAGE_META.title}</title>
        <meta name="description" content={PAGE_META.description} />
        <meta name="keywords" content={PAGE_META.keywords} />
        <meta name="author" content={PAGE_META.author} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="theme-color" content={PAGE_META.themeColor} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="image_src" href={FAVICON_URL} />
        <link rel="shortcut icon" href={FAVICON_URL} type="image/x-icon" />
        <link rel="icon" href={FAVICON_URL} />
        <meta property="og:title" content={PAGE_META.title} />
        <meta property="og:description" content={PAGE_META.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={PAGE_META.siteName} />
        <meta property="og:image" content={FAVICON_URL} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_META.title} />
        <meta name="twitter:description" content={PAGE_META.description} />
      </Helmet>

      <div className={`wlj-terms-backtotop ${showTop ? "is-visible" : ""}`}>
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      </div>

      <div className={`wlj-terms-page ${headerFixed ? "wlj-header-fixed-page" : ""}`}>
        <header className={`wlj-terms-header ${headerFixed ? "is-fixed" : ""}`}>
          <div className="wlj-header-inner">
            <div className="wlj-container wlj-mxw-1710">
              <div className="wlj-ul-between wlj-header-bar">
                <div className="wlj-header-logo">
                  <Link to="/about">
                    <img src={cdnAssetUrl("assets/welink.79ac1260.svg")} alt="Logo" />
                  </Link>
                </div>

                <div className="wlj-header-nav-wrap">
                  <nav className="wlj-header-nav">
                    <ul>
                      {NAV_LINKS.map((item) => (
                        <li key={item.label} className={item.active ? "active" : ""}>
                          {renderNavLink(item)}
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>

                <div className="wlj-header-actions">
                  <a className="wlj-btn" href="https://welinkjobs.com/NewsFeed" target="_blank" rel="noreferrer">
                    EXPLORE
                  </a>
                  <a className="wlj-btn" href="https://welinkjobs.com/signIn" target="_blank" rel="noreferrer">
                    LOGIN / SIGNUP
                  </a>
                </div>

                <button type="button" className="wlj-hamburger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                  <span className="wlj-hamburger-icon" />
                </button>
              </div>
            </div>
          </div>

          <div className={`wlj-mobile-menu ${menuOpen ? "is-open" : ""}`}>
            <div className="wlj-mobile-menu-scroll">
              <button type="button" className="wlj-mobile-close" onClick={closeMenu}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <div className="wlj-mobile-logo">
                <Link to="/about" onClick={closeMenu}>
                  <img src={cdnAssetUrl("assets/welink.79ac1260.svg")} alt="Logo" />
                </Link>
              </div>
              <ul className="wlj-mobile-nav">
                {NAV_LINKS.map((item) => (
                  <li key={item.label}>{renderNavLink(item, closeMenu)}</li>
                ))}
                <li>
                  <a href="https://welinkjobs.com/NewsFeed" target="_blank" rel="noreferrer" onClick={closeMenu}>
                    Explore
                  </a>
                </li>
                <li>
                  <a href="https://welinkjobs.com/signIn" target="_blank" rel="noreferrer" onClick={closeMenu}>
                    LOGIN / SIGNUP
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div
            className={`wlj-mobile-backdrop ${menuOpen ? "is-open" : ""}`}
            onClick={closeMenu}
            aria-hidden="true"
          />
        </header>

        <div className="wlj-terms-body-overlay" />

        <main className="wlj-terms-main">
          <section className="wlj-feature">
            <div className="wlj-container wlj-pt-50">
              <div className="wlj-row">
                <div className="wlj-col-md-12">
                  <div className="wlj-breadcrumb-content">
                    <h2 className="wlj-page-title">Terms And Condition</h2>
                  </div>
                </div>
              </div>
            </div>

            <div className="wlj-container">
              <div className="wlj-content-wrap">
                <div className="wlj-col-xl-12 wlj-col-lg-8">
                  <div className="wlj-feature-item wlj-mt-30">
                    <p>
                      Welcome, and thanks for using WeLinkjobs platform for jobs posting and related products and
                      services (the “Jobs Services”). You agree that by purchasing, signing up for, or otherwise using
                      the Jobs Services, you are entering into a legal binding agreement (even if you are using Jobs
                      Services on behalf of a company). The Jobs Services allow your postings to be served on properties
                      of WeLinkjobs and enabled third parties (collectively, "Properties"), e.g., websites and mobile
                      applications. The Jobs Services allow postings to be targeted to WeLinkjobs members based on
                      non-personally identifiable criteria (e.g., company, industry, job function, and hashed email).
                    </p>
                  </div>

                  <div className="">
                    <div className="wlj-mb-30">
                      <h2 className="wlj-section-title">
                        1. Agreement
                      </h2>
                      <div className="wlj-feature-item wlj-mt-30">
                        <p>
                          In addition to the Privacy Policy, these Terms and Conditions apply to the access and use of
                          the Jobs Services. Additional terms may apply to specific features of the Jobs Services, and
                          such terms will be presented when you engage with such features. To the extent of any conflict
                          between different agreements that you have with WeLinkjobs, these Jobs Terms and Conditions and
                          any feature-specific terms govern with respect to the use of the Jobs Services. If you are
                          accessing or using the Jobs Services on behalf of a business or third party, you represent and
                          warrant that you have the authority to do so and to bind that business or third party to these
                          Terms and Conditions, and your agreement to these Terms and Conditions will be treated as
                          their respective agreement. In this event, WeLinkjobs may hold you responsible for violations
                          of this agreement by that business or third party, and "you," "your" and "party" will also
                          refer and apply to that business or third party.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="">
                    <div className="wlj-mb-30">
                      <h2 className="wlj-section-title">
                        2. Jobs Services
                      </h2>
                      <div className="wlj-feature-item wlj-mt-30">
                        <p>
                          The Jobs Services are designed to help you reach quality candidates for job opportunities. You
                          are responsible for (i) all postings and content therefor submitted by or on your behalf
                          through the Jobs Services or otherwise to WeLinkjobs, including but not limited to the job
                          descriptions, creatives, trademarks, images, URLs, and pixels that comprise the postings or
                          content therefor (collectively, your "Postings"); and (ii) all content and property to which
                          Postings may direct viewers, as well as redirects ("Destina- tions").
                        </p>
                        <p>
                          The Jobs Services also offers the option to “sponsor” a Posting (“Sponsored Jobs”), which, for
                          an additional fee, will secure upgraded placement of the Posting on certain WeLinkjobs pages and
                          emails. WeLinkjobs will otherwise determine the size, placement and positioning of your
                          Postings. WeLinkjobs will serve your Postings as inventory becomes available on the
                          Properties. You are responsible for any activity conducted through your account, including any
                          purchases made or charges incurred. If you identify that you are using the Jobs Services on
                          behalf of a business, you agree that WeLinkjobs may share information about your purchases and
                          use of the Jobs Services with that business, including its representatives and agents. If that
                          business indicates that you are no longer authorized to place ads on its behalf, you agree you
                          may be removed from accounts associated with that business. You may not resell or transfer
                          access to the Jobs Services to any other party. Each Posting must be for one (1) job
                          opportunity; it is not permitted for a Posting to solicit applications for more than one
                          position. Without limiting the prohibitions in the User Agreement or any other applicable
                          agreement, you agree that you will not, and you will not enable or authorize any third party,
                          by virtue of the Postings, Destinations, or use of the Jobs Services, to:
                        </p>
                        <ul className="wlj-terms-list">
                          <li>
                            Create Postings without a reasonable and legitimate intent to hire for a bona fide job
                            opportunity or the specific position listed.
                          </li>
                          <li>
                            Create Postings that intentionally misrepresent the job, hiring company, or poster.
                          </li>
                          <li>
                            Fail to clearly disclose in any Posting that a position is for an independent contractor or
                            is part-time, piecework, commission-ba- sed, or has otherwise nontraditional working
                            conditions or compensation may constitute intentional misrepresentation.
                          </li>
                          <li>
                            Create Postings for “business opportunities” that require payments or recruitment of others
                            or that resemble franchises, multi-level marketing, club memberships, distributorships, or
                            are entirely or almost entirely commission based.
                          </li>
                          <li>Provide identifiable candidate resume or application data to any other parties.</li>
                          <li>
                            Spam” or otherwise contact applicants for purposes other than related to the specific
                            employment opportunity described in the posting.
                          </li>
                          <li>
                            Harass, stalk, or contact any applicant after they have asked not to be contacted.
                          </li>
                          <li>
                            Create Postings in the United States without possessing valid Federal or State Employer
                            Identification Numbers, if applicable, or create Postings in any other location in a manner
                            that would not allow compliance with applicable tax and employment laws.
                          </li>
                          <i>
                            Create Postings for jobs that require applicants to pay for employment or otherwise bear
                            costs related to employment in violation of applicable law.
                          </i>
                          <li>Soliciting employees by intentional misrepresentation.</li>
                          <li>
                            Create Postings, advertise employment positions, or otherwise engage in recruitment or
                            hiring practices that would be a violation of the law in your state or country, the state or
                            country where the job is to be performed.
                          </li>
                          <li>
                            Engage in solicitations, communications or transactions that violate any applicable laws or
                            regulations related to the prohibition of employment discrimination, or that violate applicable
                            laws governing legal eligibility to work.
                          </li>
                          <li>Engage in illegal or fraudulent conduct</li>
                          <li>
                            Except as expressly authorized by WeLinkJobs in writing, use any automated means or form of
                            scraping or data extraction to access, modify, download, query or otherwise collect
                            information from the Properties.
                          </li>
                          <li>
                            Except as expressly authorized by WeLinkjobs in writing, copy, modify or create derivative
                            works of the Jobs Services or any related technology.
                          </li>
                          <li>
                            Create Postings that contain malware, spyware or any other malicious code or otherwise
                            interfere with the operation of the Jobs Services or any device or system or breach or
                            circumvent any security measure of WeLinkjobs or a third party. WeLinkjobs may, in its sole
                            discretion, modify, or terminate any part or all of the Jobs Services or suspend your ability
                            to use the Jobs Services. WeLinkjobs may reject or remove any Posting at any time for any
                            reason, regardless of whether or not such Posting was previously accepted or served.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="">
                    <div className="wlj-mb-30">
                      <h2 className="wlj-section-title">
                        3. Fees, Payment, and Cancellation
                      </h2>
                      <div className="wlj-feature-item wlj-mt-30">
                        <p>
                          You may be offered more than one billing option when you purchase Jobs Services. You agree to
                          pay on the basis and at the rate selected when a Posting, order, or other purchase of Jobs
                          Services was submitted through your account ("Rate"). You also agree to pay all applicable
                          taxes, government charges and foreign exchange fees. WeLinkjobs will periodically charge your
                          payment method at the applicable Rate. All amounts paid are non-refundable. You remain
                          responsible for any uncollected amounts. WeLinkjobs may charge interest for any overdue amounts
                          at the rate of the lesser of 1% per month or the lawful maximum, and you agree to reimburse us
                          for all collection costs for overdue amounts. For non-flat Rates (e.g., Sponsored Jobs), the
                          amount owed will be calculated based on WeLinkjobs tracking mechanisms. WeLinkjobs is not
                          responsible for click fraud, technological issues, or other potentially invalid activity by third
                          parties that may affect the cost of running Postings. Your exclusive remedy for suspected
                          invalid activity is to make a claim for a Jobs Services credit within 90 days of the date of
                          that activity, and WeLinkjobs’s exclusive liability is in WeLinkjobs’s sole discretion, to
                          issue Jobs Services credit for suspected invalid activity. Any Jobs Services credits that you
                          may accrue are non-refundable and non-transferable and must be used prior to termination of
                          these Jobs Terms and Conditions or your account. Unless you change or remove your payment
                          method, WeLinkjobs may store and update (e.g., upon expiration) your payment method for use
                          with subsequent campaigns, orders and purchases submitted through your account.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="">
                    <div className="wlj-mb-30">
                      <h2 className="wlj-section-title">
                        4. Intellectual Property Rights and Privacy
                      </h2>
                      <div className="wlj-feature-item wlj-mt-30">
                        <p>
                          You grant to WeLinkjobs a non-exclusive, worldwide, royalty-free, sublicensable and transferable
                          license to use, copy, modify, distribute, and publish your Postings in connection with the
                          performance of the Jobs Services (including for marketing and promotional purposes). You
                          represent and warrant that you own or have all necessary rights (including intellectual
                          property rights) to your Postings (including to grant the license above).{" "}
                        </p>
                        <p>
                          If you choose to provide suggestions or feedback to WeLinkjobs about Jobs Services, you agree
                          that WeLinkjobs can (but does not have to) use and share such feedback for any purpose without
                          compensation to you. You may not disclose any information about WeLinkjobs’s pricing, metrics,
                          member demographics or beta features. WeLinkjobs reserves all of its intellectual property
                          rights in the Jobs Services as further described in the User Agreement.
                        </p>
                        <p>
                          You grant to WeLinkjobs a non-exclusive, worldwide, royalty-free, sublicensable and transferable
                          license to use, copy, modify, distribute, and publish your Postings in connection with the
                          performance of the Jobs Services (including for marketing and promotional purposes). You
                          represent and warrant that you own or have all necessary rights (including intellectual
                          property rights) to your Postings (including to grant the license above). If you choose to
                          provide suggestions or feedback to WeLinkjobs about Jobs Services, you agree that WeLinkjobs can
                          (but does not have to) use and share such feedback for any purpose without compensation to you.
                          You may not disclose any information about WeLinkjobs’s pricing, metrics, member demographics
                          or beta features. WeLinkjobs reserves all of its intellectual property rights in the Jobs
                          Services as further described in the User Agreement. Each party will comply with applicable
                          laws, self-regulatory rules, industry rules and governmental requirements (collectively,
                          "Applicable Law") relating to its performance of these terms, including privacy and security
                          laws (e.g., the EU Data Protection Directive 95/46/EC and EU Directive 2002/58/EC, each as
                          amended and implemented in various EU countries). Without limiting the generality of the
                          foregoing, each party will prominently post a complete and accurate privacy policy on its
                          websites and in its mobile applications.
                        </p>
                        <p>
                          You may use information resulting from the Jobs Services ("Jobs Services Data") only on an
                          aggregate and anonymous basis to assess the performance and effectiveness of your candidate
                          search using the Jobs Services and to optimize your candidate search. You agree to use
                          reasonable measures to protect the confidentiality of Jobs Services Data and to use appropriate
                          security safeguards to protect Jobs Services Data in accordance with industry standards. You may
                          share Jobs Services Data only with someone acting on your behalf, provided that you are
                          responsible for ensuring their respective compliance with the Agreement and for limiting their
                          respective use of the Jobs Services Data on your behalf. Without limiting the foregoing, you
                          shall not (i) sell the Jobs Services Data; (ii) combine the Jobs Services Data with any data
                          obtained by or on behalf of another employer; or (iii) except for use solely on your own behalf
                          in a manner that does not identify the Jobs Services Data as associated with WeLinkjobs,
                          transfer any Jobs Services Data (including anonymous, aggregate, or derived data) to any job
                          search service, job postings board, data broker or other monetization related service.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="wlj-mb-30">
                    <h2 className="wlj-section-title">5. Disclaimer of Warranties</h2>
                    <div className="wlj-feature-item wlj-mt-30">
                        <p>
                          To the extent allowed under law, in addition to disclaiming all implied warranties and other
                          warranties as provided in the User Agreement, WeLinkjobs and its affiliates disclaim all
                          warranties regarding the Jobs Services, including but not limited to any warranty (a) about the
                          accuracy of any candidate information (including but not limited to candidate applications,
                          candidate identities, recommendations, connections, and references); (b) concerning information
                          on which the Posting targeting categories are based (e.g. member profile information); (c)
                          concerning the rate at which inventory for your Postings becomes available on the Properties;
                          (d) concerning the results of your candidate campaigns and other uses of the Jobs Services; or
                          (e) that the Jobs Services will be uninterrupted or error-free.
                        </p>
                  </div>
                  </div>

                  <div className="wlj-mb-30">
                    <h2 className="wlj-section-title">6. Indemnity</h2>
                    <div className="wlj-feature-item wlj-mt-30">
                        <p>
                          Notwithstanding any review or approval of any Posting by WeLinkjobs, you agree to indemnify
                          WeLinkjobs and its affiliates, and hold WeLinkjobs and its affiliates harmless from any damages,
                          losses and costs (including, but not limited to attorneys' fees) related to third party claims
                          or proceedings, arising out of or related to your Postings, Destinations, use of the Jobs
                          Services, any candidate hiring or employment decisions, and breach of these Jobs Terms and
                          Conditions.
                        </p>
                  </div>
                  </div>

                  <div className="wlj-mb-30">
                    <h2 className="wlj-section-title">7. Limitation of Liability</h2>
                    <div className="wlj-feature-item wlj-mt-30">
                        <p>
                          To the fullest extent permitted by law, neither party, including its respective affiliates,
                          will be liable to the other in connection with these Jobs Terms and Conditions for lost profits
                          or lost business opportunities, loss of data, or any indirect, incidental, consequen- tial,
                          special or punitive damages
                        </p>
                        <p>
                          To the fullest extent permitted by law, neither party, including its respective affiliates,
                          will be liable to the other in connection with these Jobs Terms and Conditions for lost profits
                          or lost business opportunities, loss of data, or any indirect, incidental, consequen- tial,
                          special or punitive damages. Neither party, including its respective affiliates, will be liable
                          to the other in connection with these Jobs Terms and Conditions for an amount that exceeds the
                          greater of (a) 3 times the total fees paid or payable to WeLinkjobs during the 1-month period
                          before the event giving rise to the liability or (b) USD $100.
                        </p>
                        <p>
                          These limitations of liability do not apply to a party's (i) payment; confidentiality or
                          indemnification obligations; (ii) liability for fraud, gross negligence, or intentional
                          misconduct; (iii) liability for death or personal injury; or (iv) violation of the other party's
                          intellectual property rights.
                        </p>
                  </div>
                  </div>

                  <div className="wlj-mb-30">
                    <h2 className="wlj-section-title">8. Miscellaneous</h2>
                    <div className="wlj-feature-item wlj-mt-30">
                        <p>
                          The provisions of the User Agreement relating to feature-specific terms, termination,
                          interpretation, notice, and assignability apply to these Jobs Terms and Conditions and the Jobs
                          Services. Neither party relies on any undertaking, promise, assurance, statement, representation,
                          warranty or understanding (whether in writing or not) of any person (whether a party to these
                          Jobs Terms and Conditions or not) relating to the subject matter of these Jobs Terms and
                          Conditions, other than as stated in these Jobs Terms and Conditions. These Jobs Terms and
                          Conditions do not create a partnership, agency relationship, employer relationship or joint
                          venture between the parties.
                        </p>
                        <p>
                          WeLinkjobs may change the terms of these Jobs Terms and Conditions by notice to you (for
                          example, by a notice displayed in the Jobs Services, email, text message or mail), and both
                          parties agree that changes cannot be retroactive. If you do not agree to these changes, you
                          must stop using the Jobs Services. The following provisions of these Jobs Terms and Conditions
                          survive its termination: Sections 2 through 4, as well as Sections 6 through 8. If an issue
                          arises under these Jobs Terms and Conditions (including non-contractual disputes or claims),
                          then these Jobs Terms and Conditions are governed by the laws of Lebanon, and any action or
                          proceeding (including those arising from non-contractual disputes or claims) related to these
                          Jobs Terms and Conditions will be brought in the court of Lebanon. Each party irrevocably
                          submits to the jurisdiction and venue of the designated court. The prevailing party in any
                          litigation may seek to recover its legal fees and costs.
                        </p>
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="wlj-terms-footer wlj-pt-80 wlj-pb-25">
          <div className="wlj-container">
            <div
              className="wlj-footer-cta"
              style={{
                "--wlj-cta-shadow": `url(${cdnAssetUrl("assets/img/shape/cta_shadow.png")})`,
              }}
            >
              <div className="wlj-footer-cta-item wlj-mt-30">
                <a href="mailto:info@welinkjobs.com" className="wlj-footer-icon wlj-mr-35" aria-label="Email">
                  <FontAwesomeIcon icon={faEnvelope} />
                </a>
                <div>
                  <span className="wlj-footer-cta-label">Drop here a note</span>
                  <h3 className="wlj-footer-cta-value">info@welinkjobs.com</h3>
                </div>
              </div>

              <div className="wlj-footer-cta-item wlj-mt-30">
                <a href="tel:+13072436345" className="wlj-footer-icon wlj-mr-35" aria-label="Phone">
                  <FontAwesomeIcon icon={faPhone} />
                </a>
                <div>
                  <span className="wlj-footer-cta-label">Call us Directly</span>
                  <h3 className="wlj-footer-cta-value">+1 3072436345</h3>
                </div>
              </div>

              <span className="wlj-footer-cta-line" aria-hidden="true" />
            </div>

            <div className="wlj-footer-links wlj-mt-12">
              <div className="wlj-mt-none-40 wlj-flex-between">
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <Link to="/about">About Us</Link>
                    </li>
                    <li>
                      <a href="/about#contactform">Contact Us</a>
                    </li>
                  </ul>
                </div>
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <a href="/privacy-policy.html">Privacy Policy</a>
                    </li>
                    <li>
                      <a href="/faq.html">FAQs</a>
                    </li>
                  </ul>
                </div>
                <div className="wlj-footer-col wlj-mt-40">
                  <ul>
                    <li>
                      <Link to="/terms-and-conditions">Terms and Conditions</Link>
                    </li>
                    <li>
                      <a href="/user-agreement.html">User Agreement</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="wlj-footer-copy wlj-mt-12">
              <p className="wlj-mt-15">
                Copyright © <a href="https://welinkjobs.com/">Welinkjobs LLC</a>. All rights reserved.
              </p>
            </div>
          </div>

          <div className="wlj-footer-shapes" aria-hidden="true">
            <div className="wlj-shape wlj-shape--1">
              <img src={cdnAssetUrl("assets/img/shape/ft_shape1.png")} alt="" />
            </div>
            <div className="wlj-shape wlj-shape--2">
              <img src={cdnAssetUrl("assets/img/shape/ft_shape2.png")} alt="" />
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default TermsAndCondition;
